import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {ExpressionSignal} from '@models/adminstration/machine/trigger/expression-signal';
import {TriggerConfiguration} from '@models/adminstration/machine/trigger/trigger-configuration';
import {TriggerExpression} from '@models/adminstration/machine/trigger/trigger-expression';
import {TRIGGER_TYPE} from '@constants/trigger/trigger-type.constant';
import {TimeDependencyConfig} from '@models/adminstration/machine/trigger/time-dependency-config';
import {TriggerTimeCondition} from '@models/adminstration/machine/trigger/trigger-time-condition';
import {TriggerExpressionTypes} from '@constants/administration/machine/trigger-expression-type';
import {SignalEdge} from '@models/adminstration/machine/trigger/signal-edge';
import {switchMap, takeUntil} from 'rxjs/operators';
import {ElasticSearchResponseEntity} from '@models/elastic-search/response/elastic-search-response-entity';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {ElasticSearchSortValue} from '@models/elastic-search/request/elastic-search-sort-value';
import {ReplaySubject} from 'rxjs';
import {User} from '@models/user/user';

import {Role} from '@models/user/role';
import {SearchSelectComponent} from '@shared/search-select/search-select.component';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import { HttpResponse } from '@angular/common/http';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';

type ChangeCallbackFn<T> = (value: T) => void;
type TouchCallbackFn = () => void;

@Component({
    selector: 'scitis-admin-value-based',
    templateUrl: './value-based.component.html',
    styleUrls: ['./value-based.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ValueBasedComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ValueBasedComponent),
            multi: true
        }
    ],
    standalone: false
})
export class ValueBasedComponent implements ControlValueAccessor, Validator, OnInit, OnChanges, OnDestroy {

  @Input()
  cloudPlugs: Gateway[] = [];

  @Input()
  isLoadingCloudPlugs = false;

  @Input()
  isMachineryGroupsConfigs = false;

  @Input()
  submitted = false;

  @Input()
  isAdminOrHigher = false;

  @Input()
  isSuperAdmin = false;

  @Input()
  triggerConfiguration: TriggerConfiguration = new TriggerConfiguration();

  @Input()
  signals: ExpressionSignal[] = [];

  @Input()
  selectedTriggerType: string = '';

  @Input()
  triggerExecutionInDataflow: boolean = false;

  @Input()
  machineGroupId: number | null = null;

  @Input()
  whiteListMachinesIds: number[] | null = null;

  @Output()
  formIsValid: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  updatedTriggerConfiguration: EventEmitter<TriggerConfiguration> = new EventEmitter<TriggerConfiguration>();

  @Output()
  loadSignalsForCloudPlug: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('searchSelectInput') searchSelectInput: SearchSelectComponent | undefined;

  form: UntypedFormGroup = this.fb.group({});
  SIGNAL_EDGE: SignalEdge[] = [SignalEdge.RISING_EDGE, SignalEdge.FALLING_EDGE, SignalEdge.BOTH];
  triggerType = TRIGGER_TYPE;
  triggerTimeExpressions: TriggerExpression[] = [];
  triggerSignalsExpressions: TriggerExpression[] = [];

  private selectedOrganizationId: number | null = null;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private gatewayCount: number = 0;
  private COMPONENT_NAME = 'ValueBasedComponent';

  constructor(private fb: UntypedFormBuilder,
              private authenticationService: FirebaseAuthenticationService,
              private notification: NotificationService,
              private translation: TranslateService,
              private elasticService: ElasticSearchService) {
  }

// convenience getter for easy access to form fields
  get formControls() {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.generateForm();
    this.setTriggerTimeExpressions();
    this.setTriggerSignalsExpressions();

    if (this.isMachineryGroupsConfigs) {
      this.setAuthenticatedUserRoleAndOrganization();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedTriggerType) {
      this.generateForm();
    }

    if (!changes?.isLoadingCloudPlugs?.currentValue && this.cloudPlugs.length === 1 && this.isMachineryGroupsConfigs && this.searchSelectInput) {
      this.searchSelectInput.selectedOption = this.cloudPlugs[0];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (this.form.invalid) {
      return {invalidForm: {valid: false, message: 'ValueBasedComponent fields are invalid'}};
    }
    return null;
  }

  writeValue(val: any): void {
    if (val) {
      this.form.setValue(val, {emitEvent: false});
    }
  }

  registerOnChange(fn: ChangeCallbackFn<object>): void {
    this.form.valueChanges.subscribe(fn);
  }

  registerOnTouched(fn: TouchCallbackFn): void {
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  onLoadSignalsForCloudPlug(gateway): void {
    if (gateway) {
      this.loadSignalsForCloudPlug.emit(gateway.id);
      if (this.isMachineryGroupsConfigs) {
        this.formControls.cloudPlug?.setValue(gateway.id);
      }
    } else if (!gateway && this.isMachineryGroupsConfigs) {
      this.formControls.cloudPlug?.setValue(null);
    }
    this.formControls.cloudPlug.markAsTouched();
    this.formControls.cloudPlug.updateValueAndValidity();
  }

  onUpdatedTriggerExpressions(updatedTriggerExpressions: TriggerExpression[]) {
    this.updateTriggerExpressions(updatedTriggerExpressions);
  }

  onUpdatedTriggerTimeExpressions(updatedTriggerExpressions: TriggerExpression[]) {
    this.updateTriggerExpressions(updatedTriggerExpressions);
  }

  onDeleteTriggerExpression(triggerExpressionToDelete: TriggerExpression) {
    const index = this.triggerConfiguration.triggerExpressions.indexOf(triggerExpressionToDelete);
    this.triggerConfiguration.triggerExpressions.splice(index, 1);
  }

  onFocusOut() {
    this.onTriggerConfigUpdated();
  }

  loadGateways(term) {
    if (this.machineGroupId) {
      this.isLoadingCloudPlugs = true;
      this.elasticService.listGateways(term, new ElasticSearchSortValue(), 1, this.gatewayCount, this.selectedOrganizationId, this.machineGroupId, [], this.isSuperAdmin)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (success: ElasticSearchResponseEntity) => {
          if (success?.result?.data?.length) {
            this.cloudPlugs = success.result.data;
          }
          this.isLoadingCloudPlugs = false;
        }, error: (error) => {
          this.isLoadingCloudPlugs = false;
          const message = this.translation.instant('Error getting gateway list.');
          this.notification.error(message, error, this.COMPONENT_NAME, true);
        }
      });
    }
  }

  private generateForm(): void {
    if (this.selectedTriggerType === TRIGGER_TYPE.VALUE_BASED) {
      this.generateValueForm();
    }
  }

  private initializeSignalEdgeValue(signalEdge: string | null) {
    if (!signalEdge) {
      return this.SIGNAL_EDGE[0];
    }
    return signalEdge;
  }

  private generateValueForm(): void {
    this.form = new UntypedFormGroup({
      cloudPlug: new UntypedFormControl({
        value: this.triggerConfiguration.cloudplugId,
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      signalEdge: new UntypedFormControl({
        value: this.initializeSignalEdgeValue(this.triggerConfiguration.signalEdge),
        disabled: !this.isAdminOrHigher
      }, [Validators.required]),
      executionThreshold: new UntypedFormControl({
        value: this.triggerConfiguration.timeDependencyConfig?.actionExecutionThreshold,
        disabled: !this.isAdminOrHigher
      }, [Validators.min(0), Validators.max(7200)])

    });

    this.formIsValid.emit(!this.form.invalid);
  }

  private onTriggerConfigUpdated() {
    const triggerConfigUpdated = new TriggerConfiguration();
    triggerConfigUpdated.signalEdge = this.formControls.signalEdge.value;
    triggerConfigUpdated.cloudplugId = this.formControls.cloudPlug.value;
    if (this.selectedTriggerType === TRIGGER_TYPE.VALUE_BASED) {
      if (!triggerConfigUpdated.timeDependencyConfig) {
        triggerConfigUpdated.timeDependencyConfig = new TimeDependencyConfig();
      }
      triggerConfigUpdated.timeDependencyConfig.actionExecutionThreshold = this.formControls.executionThreshold.value;
    }
    this.form.markAsTouched();
    this.form.updateValueAndValidity();
    this.updatedTriggerConfiguration.emit(triggerConfigUpdated);
    this.formIsValid.emit(!this.form.invalid);
  }

  private setTriggerTimeExpressions() {
    this.triggerTimeExpressions = [];
    if (this.triggerConfiguration.triggerExpressions && this.triggerConfiguration.triggerExpressions.length > 0) {
      for (const expression of this.triggerConfiguration.triggerExpressions) {
        if (expression?.triggerExpressionType === TriggerExpressionTypes.TIME_EVAL) {
          if (expression.triggerTimeCondition) {
            this.createTriggerTimeCondition(expression);
            this.triggerTimeExpressions = [...this.triggerTimeExpressions, expression];
          }
        }
      }
    }
  }

  private createTriggerTimeCondition(expression: TriggerExpression) {
    if (!expression.triggerTimeCondition) {
      expression.triggerTimeCondition = new TriggerTimeCondition();
    }
  }

  private setTriggerSignalsExpressions() {
    this.triggerSignalsExpressions = [];
    if (this.triggerConfiguration.triggerExpressions && this.triggerConfiguration.triggerExpressions.length > 0) {
      for (const expression of this.triggerConfiguration.triggerExpressions) {
        if (expression.triggerExpressionType !== TriggerExpressionTypes.TIME_EVAL) {
          this.triggerSignalsExpressions = [...this.triggerSignalsExpressions, expression];
        }
      }
    }
  }

  private updateTriggerExpressions(updatedTriggerExpressions: TriggerExpression[]) {
    this.updateTriggerArray(updatedTriggerExpressions);
    this.formIsValid.emit(!this.form.invalid);
  }

  private updateTriggerArray(updatedTriggerExpressions: TriggerExpression[]) {
    for (const newExpression of updatedTriggerExpressions) {
      if (!this.triggerConfiguration.triggerExpressions.includes(newExpression)) {
        this.triggerConfiguration.triggerExpressions = [...this.triggerConfiguration.triggerExpressions, newExpression];
      }
    }
  }

  private setAuthenticatedUserRoleAndOrganization() {
    this.authenticationService.getLoggedUserDetails().pipe(takeUntil(this.destroy$), switchMap((user: User) => {
      const userRole = new Role(user);
      this.selectedOrganizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
      return this.elasticService.countGateways(this.selectedOrganizationId, this.machineGroupId, user.getWhiteListedMachineryGroups(true), this.isSuperAdmin);
    })).subscribe({
      next: res => {
        const totalHits = res?.result?.totalHits;
        if (!totalHits) {
          this.gatewayCount = 0;
        } else if (totalHits > 9999) {
          this.gatewayCount = 9999;
        } else {
          this.gatewayCount = totalHits;
        }

      }, error: (error: HttpResponse<any>) => {
        this.gatewayCount = 0;
        console.error(`${this.COMPONENT_NAME}. Error getting gateways count.`, JSON.stringify(error));
      }
    });
  }
}
